// EP-111 T111-8 — the "Set new rate" modal behind the CAF sidebar page
// (docs/ep-111-condominium-property-type.md D5/D7/D8). Only the year-to-year
// variable is editable here (rate / effective_from / vat_rate) — fee_type,
// name, and calculation_method are read-only context carried forward
// server-side (CommonAreaFee::SetRate), matching D8's series-supersede
// contract. This is NOT a raw create: the backend closes the prior open row
// of the same series and inserts the new one under a row lock, so no
// frontend math or double-submit guard is needed beyond disabling while the
// mutation is in flight.
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Modal, Button, Input, Alert } from '@/components/ui'
import { useSetCommonAreaFeeRate } from '@/hooks/useCommonAreaFees'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import type { CommonAreaFee } from '@/types'

export interface SetCafRateModalProps {
  isOpen: boolean
  onClose: () => void
  propertyId: number
  /** Any row of the series being superseded — carries the immutable context
   *  (name/fee_type/calculation_method) and the vat_rate default. */
  fee: CommonAreaFee | null
  /** Fired after a successful supersede — parent invalidates + refetches. */
  onSuccess: () => void
}

interface SetRateFormData {
  rate: number
  effective_from: string
  vat_rate: number
}

export function SetCafRateModal({ isOpen, onClose, propertyId, fee, onSuccess }: SetCafRateModalProps) {
  const { t } = useTranslation('common_area_fees')
  const { t: tc } = useTranslation('common')
  const translateError = useApiErrorTranslator('common_area_fees')
  const addToast = useNotificationStore((s) => s.addToast)
  const { timezone } = useLocalizationConfig()

  const setRate = useSetCommonAreaFeeRate()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SetRateFormData>({
    defaultValues: { rate: 0, effective_from: localToday(timezone), vat_rate: 7 },
  })

  // Re-seed the form whenever a new fee is targeted (or the modal reopens),
  // defaulting vat_rate to the current series' rate so an unrelated field
  // isn't silently reset unless the operator wants it changed.
  useEffect(() => {
    if (isOpen && fee) {
      reset({ rate: 0, effective_from: localToday(timezone), vat_rate: Number(fee.vat_rate) })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fee])

  if (!fee) return null

  const onSubmit = async (data: SetRateFormData) => {
    try {
      await setRate.mutateAsync({
        propertyId,
        id: fee.id,
        rate: Number(data.rate),
        effective_from: data.effective_from,
        vat_rate: Number(data.vat_rate),
      })
      addToast({ type: 'success', message: t('rates_page.set_rate_success') })
      onSuccess()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('rates_page.set_new_rate')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={setRate.isPending}>
            {t('rates_page.save_rate')}
          </Button>
        </>
      }
    >
      <form className="space-y-4">
        <Alert variant="info" icon={null}>
          {t('rates_page.superseding_context', {
            name: fee.name,
            method: t(`calculation_methods.${fee.calculation_method}`),
          })}
        </Alert>

        <Input
          label={t('labels.rate')}
          type="number"
          step="0.01"
          placeholder={t('placeholders.rate')}
          error={errors.rate?.message}
          {...register('rate', {
            required: t('validation.rate_required'),
            valueAsNumber: true,
            min: { value: 0.01, message: t('validation.rate_positive') },
          })}
        />

        <Input
          label={t('rates_page.new_effective_from')}
          type="date"
          error={errors.effective_from?.message}
          {...register('effective_from', {
            required: t('validation.effective_from_required'),
          })}
        />

        <Input
          label={t('labels.vat_rate')}
          type="number"
          step="0.01"
          placeholder={t('placeholders.vat_rate')}
          error={errors.vat_rate?.message}
          {...register('vat_rate', {
            valueAsNumber: true,
            min: { value: 0, message: t('validation.vat_rate_range') },
            max: { value: 100, message: t('validation.vat_rate_range') },
          })}
        />
      </form>
    </Modal>
  )
}
